"use client";
import { useContext, useEffect, useState } from "react";
import "./Profile.scss";

import { useUserContext } from "@/context/UserContext";
import Masonry from "@/components/Masonry/Masonry";
import MasonryCard from "@/components/MasonryCard/MasonryCard";
import { format } from "date-fns";
import EditProfile from "./EditProfile/EditProfile";
import { getUserComplement } from "@/services/UserService";
import { GalleryI, PhotoI } from "@/interfaces/galleries/Galleries";
import InfiniteScroll from "react-infinite-scroll-component";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner";
import { CommentI } from "@/interfaces/Admin";
import SubscriptionCard from "@/components/SubscriptionCard/SubscriptionCard";
import Comment from "@/components/Comment/Comment";
import BaseModal from "@/components/BaseModal/BaseModal";
import PointsStory from "@/components/PointsStory/PointsStory";
import { notifyError, notifySuccess } from "@/utils/NotificationFunctions";
import { handleImageError } from "@/utils/GalleryFunctions";
import SendMessageToAdmin from "@/components/SendMessageToAdmin/SendMessageToAdmin";
import { fetchUserProfile, getTotalReports } from "@/services/AuthService";
import { getPersonalCollectionsByUser } from "@/services/PersonalCollectionService";
import { useChatContext } from "@/context/InboxContext";
import { newChat } from "@/services/InboxService";
import { SocketContext } from "@/context/SocketContext";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  FormatStringDate,
  TransformToDays,
  TransformToHours,
} from "@/utils/Functions/Functionts";
import { CheckReferralPoints } from "@/utils/Functions/Points";
import { CurrentUserI } from "@/interfaces/User";
import { CopyClipboard } from "./components/CopyClipboard";
const copyIcon = "../../assets/images/icons/copy-icon.svg";
const breakpointColumnsObj = {
  default: 5,
  1100: 4,
  700: 3,
  500: 2,
  400: 1,
};
type FeedType = "contributions" | "subscriptions" | "comments";
interface Profile {
  name?: string;
}
function Profile({ name }: Profile) {
  const [feed, setFeed] = useState<FeedType>("contributions");
  const { currentUser, userToken, userLoader } = useUserContext();
  const [userProfile, setUserProfile] = useState<CurrentUserI | null>(null);
  const [editProfile, setEditProfile] = useState<boolean>(false);
  const [pointsModal, setPointsModal] = useState<boolean>(false);
  const [totalReports, setTotalReports] = useState<number>(0);
  const [pointsHistory, setPointsHistory] = useState<Array<any>>([]);
  const [messageModal, setMessageModal] = useState<boolean>(false);
  const router = useRouter();
  const [externalProfile, setExternalProfile] = useState<boolean>(false);
  const socket = useContext(SocketContext);
  const { setActiveChat, setOpenInbox } = useChatContext();
  const [data, setData] = useState<{
    contributions: { current: Array<PhotoI>; buffer: Array<PhotoI> };
    subscriptions: { current: Array<GalleryI>; buffer: Array<GalleryI> };
    comments: { current: Array<CommentI>; buffer: Array<CommentI> };
  }>({
    contributions: {
      current: [],
      buffer: [],
    },
    subscriptions: {
      current: [],
      buffer: [],
    },
    comments: {
      current: [],
      buffer: [],
    },
  });
  const [collectionsCount, setCollectionsCount] = useState<number>(0);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(
      `${
        process.env.NEXT_PUBLIC_ENV === "dev"
          ? "http://localhost:3000"
          : "https://celebstory.com"
      }?referral=${userProfile?._id}`
    );
    notifySuccess("Referral link copied successfully!");
    return;
  };
  const fetchReports = async (userId: string) => {
    try {
      const reports = await getTotalReports(userId);
      setTotalReports(reports);
    } catch {}
  };
  const fetchCollectionsCount = async (userId?: string) => {
    try {
      if (!userId) return setCollectionsCount(0);
      const res = await getPersonalCollectionsByUser(userId, userToken);
      if (res && res.status === "Success" && Array.isArray(res.data)) {
        const isSelf = currentUser?._id === userId;
        const count = isSelf
          ? res.data.length
          : res.data.filter((c) => c.status === "PUBLIC").length;
        setCollectionsCount(count);
      } else {
        setCollectionsCount(0);
      }
    } catch {
      setCollectionsCount(0);
    }
  };
  const filterPointHistory = (ph: any) => {
    const today = new Date();
    const todayInMs = today.getTime();
    const data: any[] = [];
    ph.map((p: any) => {
      if (p.status && p.status !== "removed") {
        if (p.status === "not-granted") {
          const days = TransformToDays((todayInMs - p.date) / 1000);
          if (days >= 7) {
            return;
          }
          data.push(p);
          return;
        }
        data.push(p);
        return;
      }
      if (!p.status) {
        data.push(p);
        return;
      }
    });
    return data;
  };
  const handleMoreArray = () => {
    const newBuffer = [...data[feed].buffer];
    const moreContributions = newBuffer.splice(0, 20);
    setData((prev) => ({
      ...prev,
      [feed]: {
        current: [...data[feed].current, ...moreContributions],
        buffer: newBuffer,
      },
    }));
  };

  const createChat = async () => {
    newChat(userProfile?._id || "", userToken).then((res) => {
      if (res && res.status === 200) {
        setActiveChat(res.data.chat);
        setOpenInbox(true);
        socket.emit("create-chat", res.data.chat._id);
      } else {
        notifyError("An error has ocurred");
      }
    });
  };
  const checkPendingDays = (date: number): string | null => {
    const today = new Date();
    const todayInMS = today.getTime();
    const daysBy = TransformToDays((todayInMS - date) / 1000);
    const daysLeft = 7 - daysBy;
    const hours = TransformToHours((todayInMS - date) / 1000);
    const hoursLeft = 7 * 24 - hours;
    if (daysLeft === 0) {
      return `${hoursLeft} ${hoursLeft === 1 ? "hour" : "hours"}`;
    }
    if (daysLeft < 0) {
      return null;
    }
    return `${daysLeft} ${daysLeft === 1 ? "day" : "days"}`;
  };
  const checkIfReferral = () => {
    if (pointsHistory) {
      const ph = pointsHistory.filter(
        (d) => d.reason && d.reason === "Referral"
      );
      if (ph.length > 0) {
        if (ph[0].status === "confirmed") {
          return "confirmed";
        }
        if (ph[0].status === "removed") {
          return "removed";
        }
        if (ph[0].status === "not-granted") {
          if (checkPendingDays(ph[0].date) === null) {
            return "removed";
          } else {
            return `${checkPendingDays(ph[0].date)}`;
          }
        }
      }
    }
    return null;
  };
  const handleEditProfile = () => {
    setEditProfile(true);
  };
  const getUserComplementInfo = async (externalUser?: any) => {
    if (currentUser || externalUser) {
      const userS = externalUser || currentUser;
      getUserComplement(userS._id).then((res) => {
        if (res.status === 200) {
          const tempContributions = [...res.data.contributions];
          const tempSubscriptions = [...res.data.subscriptions];
          const tempComments = [...res.data.comments];
          setData({
            subscriptions: {
              current: tempSubscriptions.splice(0, 20),
              buffer: tempSubscriptions,
            },
            contributions: {
              current: tempContributions.splice(0, 20),
              buffer: tempContributions,
            },
            comments: {
              current: tempComments.splice(0, 20),
              buffer: tempComments,
            },
          });
          setPointsHistory(res.data.pointHistory);
        }
      });
    }
  };
  function getBaseModalTitle() {
    if (pointsModal) {
      return "Points story";
    } else if (messageModal) {
      return "Send message to moderator";
    } else {
      return "";
    }
  }

  const hideModal = () => {
    setPointsModal(false);
    setMessageModal(false);
  };

  const resetStates = () => {
    setUserProfile(null);
    setData({
      contributions: {
        current: [],
        buffer: [],
      },
      subscriptions: {
        current: [],
        buffer: [],
      },
      comments: {
        current: [],
        buffer: [],
      },
    });
    setPointsHistory([]);
    setExternalProfile(false);
  };
  useEffect(() => {
    resetStates();
    if (name) {
      fetchUserProfile(name).then((res) => {
        if (res && res.status && res.status === "Success") {
          if (res.data === null) {
            router.push("/profile");
            router.push("/0");
            notifyError("That user does not exist");
          } else {
            fetchReports(res.data._id);
            fetchCollectionsCount(res.data._id);
            setUserProfile(res.data);
            getUserComplementInfo(res.data);
            setExternalProfile(true);
          }
        }
      });
    } else {
      if (currentUser) {
        fetchReports(currentUser._id);
        setUserProfile(currentUser);
        getUserComplementInfo();
        fetchCollectionsCount(currentUser._id);
      }
      if (!currentUser && !name && !userLoader) {
        router.push("/");
      }
    }
  }, [currentUser, name, userLoader]);
  const isSameUser = currentUser?._id === userProfile?._id || false;
  if (!userProfile) {
    return (
      <div className="d-flex justify-content-center align-items-center">
        <CustomSpinner size={10} borderWidth={5} />
      </div>
    );
  }
  return !editProfile ? (
    <div className="my-profile-main-container">
      {!externalProfile && (
        <BaseModal
          show={pointsModal || messageModal}
          onHide={hideModal}
          title={getBaseModalTitle()}
        >
          {pointsModal &&
            (pointsHistory.length > 0 ? (
              <div className="points-story-container">
                {filterPointHistory(pointsHistory).map(
                  (point: any, i: number) => {
                    return (
                      <PointsStory point={point} key={"point-story-" + i} />
                    );
                  }
                )}
              </div>
            ) : (
              <div>
                <h3>You don't have points already</h3>
              </div>
            ))}
          {messageModal && <SendMessageToAdmin onHide={hideModal} />}
        </BaseModal>
      )}
      <div className="user-info-container">
        <img
          src={userProfile?.avatar}
          onError={handleImageError}
          alt="user profile"
        />
        <h2>{userProfile?.name}</h2>
        <h4 className="birthdate">{userProfile?.birthdate}</h4>
        {userProfile.admin === 3 && <p>( Ambassador )</p>}
        {userProfile?.admin === 1 && <p>( Moderator )</p>}
        {!externalProfile && <h4>{userProfile?.email}</h4>}
        {((userProfile.createdAt &&
          currentUser?.admin &&
          (currentUser.admin === 1 || currentUser.admin === 2)) ||
          isSameUser) && (
          <h4>
            Date of registration: {FormatStringDate(userProfile?.createdAt)}
          </h4>
        )}
        {userProfile?.BIO && <h3>Bio: {userProfile?.BIO}</h3>}
        {userProfile?.social && <h3>Website: {userProfile?.social}</h3>}
        {userProfile?.handle && <h3>Social Handle: {userProfile?.handle}</h3>}
        {userProfile?.tiktok && <h3>Tiktok: {userProfile?.tiktok}</h3>}
        {userProfile?.twitter && <h3>Twitter: {userProfile?.twitter}</h3>}
        {userProfile?.instagram && <h3>Instagram: {userProfile?.instagram}</h3>}
        {userProfile?.facebook && <h3>Facebook: {userProfile?.facebook}</h3>}
        <h3>
          <span>{userProfile?.points}</span> points
        </h3>
        {userProfile?.admin === 1 &&
          (currentUser?.admin === 2 ||
            currentUser?._id === userProfile?._id) && (
            <h3>
              <span>{userProfile?.editCredits}</span> edit credits
            </h3>
          )}
        <h4>
          Reports: <span>{totalReports}</span>
        </h4>
        <h4>
          Points available: <span>{userProfile?.availablePoints}</span>
        </h4>
        {checkIfReferral() !== null && (
          <h4 style={{ textAlign: "center" }}>
            Bonus status:{" "}
            <span className={`${checkIfReferral()}`}>{checkIfReferral()}</span>{" "}
            {!(
              checkIfReferral() === "confirmed" ||
              checkIfReferral() === "removed"
            ) && (
              <>
                left to protect your 500 bonus, by gaining{" "}
                <span>{CheckReferralPoints(pointsHistory)}</span> points more
                from actions with the content, like uploading.
              </>
            )}
          </h4>
        )}
        {!externalProfile ? (
          <>
            <button
              className="primary-action-btn"
              onClick={() => setPointsModal(true)}
            >
              Points Story
            </button>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 8,
              }}
            >
              <button
                className="primary-action-btn flex-col"
                onClick={() => router.push("/collections")}
              >
                My Collections
                <br />
                {collectionsCount > 0 && (
                  <span style={{ opacity: 0.8 }}>
                    {collectionsCount} available
                  </span>
                )}
              </button>
            </div>
          </>
        ) : (
          <>
            <button className="primary-action-btn" onClick={createChat}>
              Send message
            </button>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginTop: 8,
              }}
            >
              <button
                className="primary-action-btn"
                onClick={() =>
                  router.push(
                    `/collections?user=${encodeURIComponent(
                      userProfile?.name || userProfile?._id || ""
                    )}`
                  )
                }
              >
                Collections
                <br />
                <span style={{ opacity: 0.8 }}>
                  {collectionsCount} collection
                  {collectionsCount === 1 ? "" : "s"}
                </span>
              </button>
              <span style={{ opacity: 0.8 }}></span>
            </div>
          </>
        )}
        <hr />
        {currentUser &&
          (currentUser?.admin === 1 || currentUser?.admin === 2) && (
            <h3 className="member-since">
              Member since{" "}
              {userProfile
                ? format(new Date(userProfile.createdAt), "MMMM d, yyyy")
                : ""}
            </h3>
          )}
        <div className="referral-container">
          <h4 className="referral-link">
            Referral Link: /?referral=
            {userProfile?._id}
          </h4>
          <button onClick={copyToClipboard}>
            <img src={copyIcon} />
          </button>
        </div>
        {!externalProfile && (
          <>
            <button className="primary-action-btn" onClick={handleEditProfile}>
              Edit profile
            </button>
            <button
              className="primary-action-btn"
              onClick={() => setMessageModal(true)}
            >
              Send Message to Moderator
            </button>
          </>
        )}
        <CopyClipboard
          link={`https://www.celebstory.com/profile?user=${userProfile?.name}`}
        />
        <hr />
      </div>
      <div className="feed-container">
        <div className="header-nav-container">
          <h2
            role="button"
            className={feed === "contributions" ? "active" : ""}
            onClick={() => setFeed("contributions")}
          >
            Contributions
          </h2>
          <h2
            role="button"
            className={feed === "subscriptions" ? "active" : ""}
            onClick={() => setFeed("subscriptions")}
          >
            Subscriptions
          </h2>
          <h2
            role="button"
            className={feed === "comments" ? "active" : ""}
            onClick={() => setFeed("comments")}
          >
            Comments
          </h2>
        </div>
        <InfiniteScroll
          dataLength={data[feed].current.length}
          next={handleMoreArray}
          hasMore={data[feed].buffer.length > 0}
          loader={
            <div
              className="d-flex w-100 justify-content-center align-items-center"
              style={{ overflow: "hidden" }}
            >
              <CustomSpinner size={8} borderWidth={4} />
            </div>
          }
        >
          {feed === "contributions" && (
            <Masonry
              title=""
              loading={false}
              breakpointColumnsObj={breakpointColumnsObj}
            >
              {data.contributions.current.map((photo: PhotoI, i: number) => {
                return (
                  <MasonryCard
                    gallery={{ ...photo, avatar: photo.url, name: photo.name }}
                    photo_id={photo._id}
                    key={`contribution-image-${i}`}
                  />
                );
              })}
            </Masonry>
          )}
          {feed === "subscriptions" && (
            <div className="subscriptions-cards-container">
              {data.subscriptions.current.map((gallery) => {
                return <SubscriptionCard gallery={gallery} key={gallery._id} />;
              })}
            </div>
          )}
          {feed === "comments" && (
            <div className="subscriptions-cards-container">
              {data.comments.current.map((comment) => {
                return (
                  <Link
                    href={`/gallery/${comment?.photoId?.collectionId}?p=${comment?.photoId?._id}&year=${comment?.photoId?.year}`}
                    key={comment._id}
                  >
                    <Comment viewOnly={true} comment={comment} />
                  </Link>
                );
              })}
            </div>
          )}
        </InfiniteScroll>
      </div>
    </div>
  ) : (
    <EditProfile setEditProfile={setEditProfile} />
  );
}

export default Profile;
